/**
 * THE MONEY VIEW OF A WORK ORDER'S LINES — value, GST split and total, per
 * line, with a totals row checked against the order header. Asked by the CMD,
 * 31-10-2026. Used by the "Lines" card and the "Machines on hire" card, behind
 * a Progress / Money & GST switch so neither table grows past a screen.
 *
 * The arithmetic is in ../lib/gst.ts; this only draws it.
 */
import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Landmark } from 'lucide-react'
import { rupees } from '@/lib/format'
import type { GstHeader, GstSplit } from '../lib/gst'

export interface GstRow {
  key: string
  sl: number
  title: string
  subtitle?: string | null
  split: GstSplit
}

const n = (v: unknown) => Number(v ?? 0) || 0

type View = 'PROGRESS' | 'MONEY'
const STORE = 'oval.wo.lines.view'

/**
 * Which view a card opens in. An order awaiting approval opens on the money -
 * that is what the person approving it is deciding on. Otherwise it opens on
 * whatever this person last chose, else progress, as it always has.
 */
export function useLinesView(status: string | undefined): [View, (v: View) => void] {
  const [view, setView] = useState<View>(() => {
    try { return (localStorage.getItem(STORE) as View) === 'MONEY' ? 'MONEY' : 'PROGRESS' }
    catch { return 'PROGRESS' }
  })
  // The status often arrives a moment after the card first draws; switch to
  // the money once, when it turns out to be awaiting approval, and leave the
  // person's own choice alone after that.
  const settled = useRef(false)
  useEffect(() => {
    if (settled.current || !status) return
    settled.current = true
    if (status === 'SUBMITTED') setView('MONEY')
  }, [status])
  return [view, (v: View) => {
    setView(v)
    try { localStorage.setItem(STORE, v) } catch { /* private window: fine */ }
  }]
}

export function LinesViewSwitch({ view, onChange, progressLabel = 'Progress' }: {
  view: View
  onChange: (v: View) => void
  progressLabel?: string
}) {
  const btn = (v: View, label: string) => (
    <button type="button" onClick={() => onChange(v)} aria-pressed={view === v}
            className={view === v
              ? 'rounded-md bg-oval-600 px-2.5 py-1 text-xs font-medium text-white'
              : 'rounded-md px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100'}>
      {label}
    </button>
  )
  return (
    <span className="inline-flex gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5">
      {btn('PROGRESS', progressLabel)}
      {btn('MONEY', 'Money & GST')}
    </span>
  )
}

export function LineGstTable({ rows, header, treatment }: {
  rows: GstRow[]
  header: GstHeader & { contract_value: number; total_with_gst: number }
  treatment: string | null
}) {
  const igst = treatment === 'IGST'
  const sum = (f: (s: GstSplit) => number) =>
    Math.round(rows.reduce((a, r) => a + f(r.split), 0) * 100) / 100
  const value = sum((s) => s.value)
  const charged = sum((s) => (s.payer === 'CHARGED' ? s.gst : 0))
  const rcm = sum((s) => (s.payer === 'RCM' ? s.gst : 0))
  const total = sum((s) => s.total)
  const agrees = Math.abs(value - n(header.contract_value)) < 0.01
    && Math.abs(charged - n(header.gst_amount)) < 0.01

  return (
    <>
      <div className="overflow-x-auto">
        <table className="tbl">
          <thead>
            <tr>
              <th>#</th><th>Item</th>
              <th className="num">Value</th><th className="num">GST %</th>
              {igst
                ? <th className="num">IGST</th>
                : <><th className="num">CGST</th><th className="num">SGST</th></>}
              <th className="num">GST</th>
              <th className="num">Total incl. GST</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>{r.sl}</td>
                <td className="max-w-[260px]">
                  {r.title}
                  {r.subtitle && <span className="block text-[11px] text-slate-400">{r.subtitle}</span>}
                </td>
                <td className="num whitespace-nowrap">{rupees(r.split.value)}</td>
                <td className="num">{r.split.payer === 'NONE' ? '—' : `${r.split.rate}%`}</td>
                {igst
                  ? <td className="num whitespace-nowrap">{r.split.igst ? rupees(r.split.igst) : '—'}</td>
                  : <>
                      <td className="num whitespace-nowrap">{r.split.cgst ? rupees(r.split.cgst) : '—'}</td>
                      <td className="num whitespace-nowrap">{r.split.sgst ? rupees(r.split.sgst) : '—'}</td>
                    </>}
                <td className="num whitespace-nowrap">
                  {r.split.payer === 'NONE' ? '—' : rupees(r.split.gst)}
                  {r.split.payer === 'RCM' && (
                    <span className="pill-amber ml-1" title="Reverse charge: OVAL pays this to the Government, not to the contractor">
                      RCM
                    </span>
                  )}
                </td>
                <td className="num whitespace-nowrap font-semibold">{rupees(r.split.total)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-300 font-semibold">
              <td />
              <td>Total</td>
              <td className="num whitespace-nowrap">{rupees(value)}</td>
              <td />
              {igst
                ? <td className="num whitespace-nowrap">{rupees(sum((s) => s.igst))}</td>
                : <>
                    <td className="num whitespace-nowrap">{rupees(sum((s) => s.cgst))}</td>
                    <td className="num whitespace-nowrap">{rupees(sum((s) => s.sgst))}</td>
                  </>}
              <td className="num whitespace-nowrap">{rupees(charged)}</td>
              <td className="num whitespace-nowrap">{rupees(total)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-3 space-y-1.5 text-xs">
        {rcm > 0 && (
          <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-amber-900">
            <Landmark className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              <strong>{rupees(rcm)}</strong> of GST on lines marked RCM is paid by OVAL to the
              Government under reverse charge. It is not added to the contractor&rsquo;s total
              and not deducted from it.
            </span>
          </p>
        )}
        {agrees ? (
          <p className="flex items-center gap-1.5 text-teal-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Agrees with the order: value {rupees(header.contract_value)}, GST{' '}
            {rupees(header.gst_amount)}, total {rupees(header.total_with_gst)}.
          </p>
        ) : (
          <p className="text-amber-800">
            The order header reads value {rupees(header.contract_value)} and GST{' '}
            {rupees(header.gst_amount)}. A difference of a few paise is rounding; anything more,
            tell the administrator.
          </p>
        )}
      </div>
    </>
  )
}
